<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import OrganizationStorageInfo from '@/components/organizations/OrganizationStorageInfo.vue';
import { Feature } from '@/enums/feature';
import { useAuthStore } from '@/stores/auth';
import { useModuleStore } from '@/stores/module';
import { useOrganizationStore } from '@/stores/organization';
import type { Module } from '@/types/module';
import { TypeOfOrganization, type Organization, type OrganizationForm } from '@/types/organization';
import { getEnumKeyValues } from '@/utils/enum';
import { required } from '@/utils/validation';
import { onMounted, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const props = defineProps<{
    parent?: string;
    id?: string;
}>();
const router = useRouter();
const { t: translate } = useI18n();

const organizationStore = useOrganizationStore();
const moduleStore = useModuleStore();
const authStore = useAuthStore();

const organization = ref<Organization>({ type: TypeOfOrganization.COMPANY } as Organization);
const modules = ref<Module[]>();

const organizations = ref<Organization[]>([]);
const form = useTemplateRef('form');
const isLoading = ref(false);

onMounted(async () => {
    if (props.id) {
        const org = await organizationStore.fetchById(props.id);
        if (!org) return;

        organization.value = org;
    }

    if (props.parent) {
        organization.value.parent = props.parent;
        organization.value.type = TypeOfOrganization.BRANCH;
    }

    if (authStore.isSuperAdmin()) {
        modules.value = await moduleStore.getAll();

        if (organization.value.type === TypeOfOrganization.BRANCH) {
            organizations.value = await organizationStore.get();
        }

        if (!props.parent && organization.value.modules.length === 0) {
            const rieModule = modules.value.find((m) => m.name === 'RI&E');
            if (rieModule) organization.value.modules = [rieModule._id];
        }
    }
});

async function createOrganization() {
    const result = await organizationStore.create(organization.value as OrganizationForm);
    return result.object as Organization;
}

async function updateOrganization(organization: Organization) {
    await organizationStore.update(organization);
}

async function handleForm() {
    const formValidation = await form.value?.validate();
    if (!formValidation?.valid) return;

    isLoading.value = true;
    try {
        if (!props.id) {
            const createdOrganization = await createOrganization();
            if (createdOrganization) await router.push(`/bedrijf/${createdOrganization?._id}/informatie`);
        } else {
            await updateOrganization(organization.value);
            router.back();
        }
    } finally {
        isLoading.value = false;
    }
}
</script>

<template>
    <v-form ref="form">
        <v-text-field v-model="organization.name" label="Naam" required :rules="[required]"/>
        <v-select
            v-if="authStore.isSuperAdmin()"
            :items="Object.values(TypeOfOrganization)"
            v-model="organization.type"
            label="Type"
            required
            :rules="[required]"
            :disabled="organization.parent != null && !authStore.isSuperAdmin()"
        />
        <v-autocomplete
            v-if="organization.type === TypeOfOrganization.BRANCH"
            :items="authStore.isSuperAdmin() ? organizations : authStore.accessibleOrganizations"
            v-model="organization.parent"
            label="Bovenliggend bedrijf"
            item-title="name"
            item-value="_id"
            :rules="[required]"
            required
            :disabled="props.parent != null"
        />

        <template v-if="authStore.isSuperAdmin() && organization.type === TypeOfOrganization.COMPANY">
            <v-autocomplete
                v-model="organization.modules"
                multiple
                :items="modules"
                label="Modules"
                item-title="name"
                item-value="_id"
                clearable
                :rules="[required]"
                required
            />
            <OrganizationStorageInfo
                v-if="organization._id"
                formFactor="label"
                :currentUploadSizeMb="organization.currentUploadSizeMb"
                :maxUploadSizeMb="organization.maxUploadSizeMb"
                showProgressAsPercentage
                :progressBarHeight="20"
                progressBar
            />
            <v-text-field v-model="organization.maxUploadSizeMb" label="Maximale opslag (MB)" type="number" min="0"/>

            <h3>Features</h3>
            <v-checkbox
                v-for="(featurePair, index) in getEnumKeyValues(Feature)"
                v-model="organization.features"
                :value="index"
                :key="index"
                :label="translate(`enums.Feature.${featurePair[0]}`)"
                density="compact"
                hide-details
            />
        </template>
    </v-form>

    <CustomButton class="mt-2" @click="handleForm()" text="Opslaan" />
</template>
